/***************************************************************************************************

 Copyright 2023-2024 Fannie Mae. All rights reserved.
 This software contains confidential information and trade secrets of Fannie Mae.
 Use, disclosure, or reproduction is prohibited without the prior written consent of Fannie Mae.

 ****************************************************************************************************/

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppConstants } from "../shared/app.constants";
import { SelectedFiles } from "../model/SelectedFiles";
import { DocWayDetailsService } from "./docwaydetails.service";
import { EndpointsService } from "./endpoints.service";
import { ApiService } from "./api.service";
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileUploadDetails } from '../model/FileUploadDetails';
import { FileUploadComponent } from '../due-diligence/component/file-upload/file-upload.component';
import { FileUploadInsuranceComponent } from '../insurance/component/file-upload-insurance/file-upload-insurance.component';
import { DashboardService } from './dashboard.service';

const isNumeric = (val: any): boolean => {
  if (val === null || val === undefined) return false;
  return /^\d+$/.test(String(val));
};

@Injectable({
  providedIn: 'root'
})

export class FileUploadService {
  private fileUploadInsuranceComponent: FileUploadInsuranceComponent;
  private fileUploadComponent: FileUploadComponent;

  disableUpload: boolean;
  propertyMatch: boolean;
  propertyIDMatch: boolean;
  propertyNameMatch: boolean;
  reportTypeMatch: boolean;
  fileTypeMatch: boolean;
  uploadUrl = '';
  statusUrl = '';
  rejectedFiles: any = [];
  selectedFileReportType: string;
  selectedFilePropertyName: string;
  selectedFileFormat: string;
  selectedFileDealId: string;
  selectedFiles: SelectedFiles[] = [];
  fileSelected = false;
  isDocwayUser = true;

  private fileUploadDetails = {
    source: '',
    completed: false,
  } as FileUploadDetails;

  private reviewFileSummaryTableDataSubject = new BehaviorSubject<any>(null);
  reviewFileSummaryTableData$ = this.reviewFileSummaryTableDataSubject.asObservable();

  constructor(
    private docWayDetailsService: DocWayDetailsService,
    private httpClient: HttpClient,
    private endpointsService: EndpointsService,
    private apiService: ApiService,
    private dashboardService: DashboardService
  ) {
  }

  // Methods to set and get FileUploadComponent and FileUploadInsuranceComponent so that they can be accessed in 
  // IssueSummaryPreviewComponent for print preview and error summary display
  public setFileUploadInsuranceComponent(component: FileUploadInsuranceComponent) {
    this.fileUploadInsuranceComponent = component;
  }

  public getFileUploadInsuranceComponent(): FileUploadInsuranceComponent {
    return this.fileUploadInsuranceComponent;
  }

  // Since FileUploadComponent is used more widely across different report types, created a separate method 
  // for it in case we want to have different logic for insurance file upload component in the future
  public setFileUploadComponent(component: FileUploadComponent) {
    this.fileUploadComponent = component;
  }

  public getFileUploadComponent(): FileUploadComponent {
    return this.fileUploadComponent;
  }

  public setSource(source: string) {
    this.fileUploadDetails.source = source;
    this.fileUploadDetails.completed = false;
  }

  public getSource() {
    return this.fileUploadDetails.source;
  }

  public setCompleted(completed: boolean) {
    this.fileUploadDetails.completed = completed;
  }

  public getFileUploadDetails() {
    return this.fileUploadDetails;
  }

  // Show DocWay authorization error via the component's dialog
  private showDocwayAuthError(header: string, errMsg: string, errorNote: string) {
    if (this.fileUploadInsuranceComponent) {
      this.fileUploadInsuranceComponent.showConfirmDialog(
        header,
        errMsg,
        errorNote
      );
    }
  }

  // PCA/ESA/SRA/ZON/APPR/BPO
  processSelectedFiles_All(event: any) {
    let selectedFileReportTypeTemp: string;
    let selectedFileDealIdTemp: string;
    this.disableUpload = false;
    this.rejectedFiles = [];

    for (let i = 0; i < event.files.length; i++) {
      //Make sure ReportType is either PCA, ESA, SRA, ZON and check with Docway value if available
      selectedFileReportTypeTemp = event.files[i].name.split(".")[0];
      this.selectedFileFormat = event.files[i].name.split(".")[1].toUpperCase();
      selectedFileReportTypeTemp = selectedFileReportTypeTemp.split("_")[1];
      if (selectedFileReportTypeTemp) {
        if (this.docWayDetailsService.hasReportType) {
          if (selectedFileReportTypeTemp.toUpperCase() == "ZON" && this.docWayDetailsService.mf_doc_type == "Zoning Report")
            this.selectedFileReportType = "ZON";
          else if (selectedFileReportTypeTemp.toUpperCase() == "SRA" && this.docWayDetailsService.mf_doc_type == "Seismic Risk Assessment")
            this.selectedFileReportType = "SRA";
          else if (selectedFileReportTypeTemp.toUpperCase() == "ESA" && this.docWayDetailsService.mf_doc_type == "Environmental Site Assessment")
            this.selectedFileReportType = "ESA";
          else if (selectedFileReportTypeTemp.toUpperCase() == "PCA" && this.docWayDetailsService.mf_doc_type == "Property Condition Assessment")
            this.selectedFileReportType = "PCA";
          else {
            this.selectedFileReportType = "";
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
        } else {
          if (selectedFileReportTypeTemp.toUpperCase() == "PCA" ||
            selectedFileReportTypeTemp.toUpperCase() == "ESA" ||
            selectedFileReportTypeTemp.toUpperCase() == "SRA" ||
            selectedFileReportTypeTemp.toUpperCase() == "ZON" ||
            selectedFileReportTypeTemp.toUpperCase() == "BPO" ||
            selectedFileReportTypeTemp.toUpperCase() == "APPR") {
            this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
          } else {
            this.selectedFileReportType = "";
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
        }
      } else {
        this.selectedFileReportType = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Check if APPR file is XML
      if (this.selectedFileReportType == "APPR" && this.selectedFileFormat != "XML") {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Check whether file is TPR or BPO/APPR and validate Deal ID or CRN based on that
      if (this.selectedFileReportType == "BPO") {
        //Make sure CRN is numeric and 10 digits long
        selectedFileDealIdTemp = event.files[i].name.split("_")[0];
        var crnRegExp_1 = new RegExp('^[0-9]{10}$');
        var crnRegExp_2 = new RegExp('^[0-9]{10}-[0-9]{3}$');
        if (crnRegExp_1.test(selectedFileDealIdTemp) || crnRegExp_2.test(selectedFileDealIdTemp)) {
          this.selectedFileDealId = selectedFileDealIdTemp;
        } else {
          this.selectedFileDealId = "";
          if (!this.disableUpload)
            this.rejectedFiles.push(event.files[i].name);
          this.disableUpload = true;
        }
      } else {
        //Make sure DealID is numeric and check Deal ID
        selectedFileDealIdTemp = event.files[i].name.split("_")[0];
        if (this.selectedFileReportType == "BPO") {
          if (isNumeric(selectedFileDealIdTemp) && selectedFileDealIdTemp.length == 10) {
            this.selectedFileDealId = selectedFileDealIdTemp;
          }
        } else {
          if (isNumeric(selectedFileDealIdTemp) &&
            (!this.docWayDetailsService.hasDealId)) {
            this.selectedFileDealId = selectedFileDealIdTemp;
          } else if (this.docWayDetailsService.hasDealId &&
            (selectedFileDealIdTemp == this.docWayDetailsService.mf_deal_id) &&
            isNumeric(selectedFileDealIdTemp)) {
            this.selectedFileDealId = selectedFileDealIdTemp;
          } else {
            this.selectedFileDealId = "";
            if (!this.disableUpload)
              this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
        }
      }

      //Add selected files to table
      this.selectedFiles.push({
        fileName: event.files[i].name,
        fileType: event.files[i].name.split(".")[event.files[i].name.split(".").length - 1],
        reportType: this.selectedFileReportType,
        dealId: this.selectedFileDealId
      });

      //Limit number of files uploaded per upload
      if (this.selectedFiles.length > AppConstants.MAX_FILE_UPLOAD_LIMIT)
        this.disableUpload = true;
    }
  }

  // PCA/ESA/SRA/ZON/APPR
  processSelectedFiles_ThirdPartyReports_Appraisal(event: any) {
    let selectedFileReportTypeTemp: string;
    let selectedFileDealIdTemp: string;
    this.disableUpload = false;
    this.rejectedFiles = [];

    for (let i = 0; i < event.files.length; i++) {
      //Make sure ReportType is either PCA, ESA, SRA, ZON and check with Docway value if available
      selectedFileReportTypeTemp = event.files[i].name.split(".")[0];
      this.selectedFileFormat = event.files[i].name.split(".")[1].toUpperCase();
      selectedFileReportTypeTemp = selectedFileReportTypeTemp.split("_")[1];
      if (selectedFileReportTypeTemp) {
        if (this.docWayDetailsService.hasReportType) {
          if (selectedFileReportTypeTemp.toUpperCase() == "ZON" && this.docWayDetailsService.mf_doc_type == "Zoning Report")
            this.selectedFileReportType = "ZON";
          else if (selectedFileReportTypeTemp.toUpperCase() == "SRA" && this.docWayDetailsService.mf_doc_type == "Seismic Risk Assessment")
            this.selectedFileReportType = "SRA";
          else if (selectedFileReportTypeTemp.toUpperCase() == "ESA" && this.docWayDetailsService.mf_doc_type == "Environmental Site Assessment")
            this.selectedFileReportType = "ESA";
          else if (selectedFileReportTypeTemp.toUpperCase() == "PCA" && this.docWayDetailsService.mf_doc_type == "Property Condition Assessment")
            this.selectedFileReportType = "PCA";
          else {
            this.selectedFileReportType = "";
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
        } else {
          if (selectedFileReportTypeTemp.toUpperCase() == "PCA" ||
            selectedFileReportTypeTemp.toUpperCase() == "ESA" ||
            selectedFileReportTypeTemp.toUpperCase() == "SRA" ||
            selectedFileReportTypeTemp.toUpperCase() == "ZON" ||
            selectedFileReportTypeTemp.toUpperCase() == "APPR") {
            this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
          } else if (selectedFileReportTypeTemp.toUpperCase() == "BPO") {
            this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          } else {
            this.selectedFileReportType = "";
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
        }
      } else {
        this.selectedFileReportType = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Check if APPR file is XML
      if (this.selectedFileReportType == "APPR" && this.selectedFileFormat != "XML") {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Validate Deal ID
      //Make sure DealID is numeric and check Deal ID
      selectedFileDealIdTemp = event.files[i].name.split("_")[0];
      if (isNumeric(selectedFileDealIdTemp) &&
        (!this.docWayDetailsService.hasDealId)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else if (this.docWayDetailsService.hasDealId &&
        (selectedFileDealIdTemp == this.docWayDetailsService.mf_deal_id) &&
        isNumeric(selectedFileDealIdTemp)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Add selected files to table
      this.selectedFiles.push({
        fileName: event.files[i].name,
        fileType: event.files[i].name.split(".")[event.files[i].name.split(".").length - 1],
        reportType: this.selectedFileReportType,
        dealId: this.selectedFileDealId
      });

      //Limit number of files uploaded per upload
      if (this.selectedFiles.length > AppConstants.MAX_FILE_UPLOAD_LIMIT)
        this.disableUpload = true;
    }
  }

  // PCA/ESA/SRA/ZON/APPR and Property Specific Check
  processSelectedFiles_ThirdPartyReports_Appraisal_PropertySpecific(event: any, dusDealId: string, userRoles: any) {
    let selectedFileReportTypeTemp: string;
    let selectedFileDealIdTemp: string;
    this.disableUpload = false;
    this.propertyMatch = true;
    this.rejectedFiles = [];

    for (let i = 0; i < event.files.length; i++) {
      //Make sure ReportType is either PCA, ESA, SRA, ZON and check with Docway value if available
      selectedFileReportTypeTemp = event.files[i].name.split(".")[0];
      this.selectedFileFormat = event.files[i].name.split(".")[1].toUpperCase();
      selectedFileReportTypeTemp = selectedFileReportTypeTemp.split("_")[1];
      if (selectedFileReportTypeTemp) {
        if (this.docWayDetailsService.hasReportType) {
          if (selectedFileReportTypeTemp.toUpperCase() == "ZON" && this.docWayDetailsService.mf_doc_type == "Zoning Report")
            this.selectedFileReportType = "ZON";
          else if (selectedFileReportTypeTemp.toUpperCase() == "SRA" && this.docWayDetailsService.mf_doc_type == "Seismic Risk Assessment")
            this.selectedFileReportType = "SRA";
          else if (selectedFileReportTypeTemp.toUpperCase() == "ESA" && this.docWayDetailsService.mf_doc_type == "Environmental Site Assessment")
            this.selectedFileReportType = "ESA";
          else if (selectedFileReportTypeTemp.toUpperCase() == "PCA" && this.docWayDetailsService.mf_doc_type == "Property Condition Assessment")
            this.selectedFileReportType = "PCA";
          else {
            this.selectedFileReportType = "";
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
        } else {
          if (selectedFileReportTypeTemp.toUpperCase() == "PCA" ||
            selectedFileReportTypeTemp.toUpperCase() == "ESA" ||
            selectedFileReportTypeTemp.toUpperCase() == "SRA" ||
            selectedFileReportTypeTemp.toUpperCase() == "ZON" ||
            selectedFileReportTypeTemp.toUpperCase() == "APPR") {
            this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
          } else if (selectedFileReportTypeTemp.toUpperCase() == "BPO") {
            this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          } else {
            this.selectedFileReportType = "";
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
          if (userRoles.isAssetManager && (this.selectedFileReportType == "ESA" || this.selectedFileReportType == "SRA" || this.selectedFileReportType == "ZON")) {
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
        }
      } else {
        this.selectedFileReportType = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Check if APPR file is XML
      if (this.selectedFileReportType == "APPR" && this.selectedFileFormat != "XML") {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Validate Deal ID
      //Make sure DealID is numeric and check Deal ID
      selectedFileDealIdTemp = event.files[i].name.split("_")[0];
      if (isNumeric(selectedFileDealIdTemp) &&
        (!this.docWayDetailsService.hasDealId)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else if (this.docWayDetailsService.hasDealId &&
        (selectedFileDealIdTemp == this.docWayDetailsService.mf_deal_id) &&
        isNumeric(selectedFileDealIdTemp)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Make sure DealID matches selected property
      if (dusDealId == selectedFileDealIdTemp) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
        this.propertyMatch = false;
      }

      if (this.disableUpload) {
        //Add selected files to table
        this.selectedFiles.push({
          fileName: event.files[i].name,
          fileType: event.files[i].name.split(".")[event.files[i].name.split(".").length - 1],
          reportType: this.selectedFileReportType,
          dealId: this.selectedFileDealId
        });
      }

      //Limit number of files uploaded per upload
      if (this.selectedFiles.length > AppConstants.MAX_FILE_UPLOAD_LIMIT)
        this.disableUpload = true;
    }
  }

  //BPO
  processSelectedFiles_BPO(event: any) {
    let selectedFileReportTypeTemp: string;
    let selectedFileDealIdTemp: string;
    this.disableUpload = false;
    this.rejectedFiles = [];

    for (let i = 0; i < event.files.length; i++) {
      //Make sure ReportType is BPO
      selectedFileReportTypeTemp = event.files[i].name.split(".")[0];
      selectedFileReportTypeTemp = selectedFileReportTypeTemp.split("_")[1];
      if (selectedFileReportTypeTemp) {
        if (selectedFileReportTypeTemp.toUpperCase() == "BPO") {
          this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
        } else if (selectedFileReportTypeTemp.toUpperCase() == "PCA" ||
          selectedFileReportTypeTemp.toUpperCase() == "ESA" ||
          selectedFileReportTypeTemp.toUpperCase() == "SRA" ||
          selectedFileReportTypeTemp.toUpperCase() == "ZON") {
          this.selectedFileReportType = "TPR";
          this.rejectedFiles.push(event.files[i].name);
          this.disableUpload = true;
        } else if (selectedFileReportTypeTemp.toUpperCase() == "APPR") {
          this.selectedFileReportType = "APPR";
          this.rejectedFiles.push(event.files[i].name);
          this.disableUpload = true;
        } else {
          this.selectedFileReportType = "";
          this.rejectedFiles.push(event.files[i].name);
          this.disableUpload = true;
        }
      } else {
        this.selectedFileReportType = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Make sure CRN is numeric and 10 digits long
      selectedFileDealIdTemp = event.files[i].name.split("_")[0];
      var crnRegExp_1 = new RegExp('^[0-9]{10}$');
      var crnRegExp_2 = new RegExp('^[0-9]{10}-[0-9]{3}$');
      if (crnRegExp_1.test(selectedFileDealIdTemp) || crnRegExp_2.test(selectedFileDealIdTemp)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Add selected files to table
      this.selectedFiles.push({
        fileName: event.files[i].name,
        fileType: event.files[i].name.split(".")[event.files[i].name.split(".").length - 1],
        reportType: this.selectedFileReportType,
        dealId: this.selectedFileDealId
      });

      //Limit number of files uploaded per upload
      if (this.selectedFiles.length > AppConstants.MAX_FILE_UPLOAD_LIMIT)
        this.disableUpload = true;
    }
  }


  //PCA No Prop
  processSelectedFiles_PCANoProp(event: any) {
    let selectedFileReportTypeTemp: string;
    let selectedFileDealIdTemp: string;
    this.disableUpload = false;
    this.rejectedFiles = [];

    for (let i = 0; i < event.files.length; i++) {
      selectedFileReportTypeTemp = event.files[i].name.split(".")[0];
      selectedFileReportTypeTemp = selectedFileReportTypeTemp.split("_")[1];

      if (selectedFileReportTypeTemp.toUpperCase() == "PCA" ||
        selectedFileReportTypeTemp.toUpperCase() == "ESA" ||
        selectedFileReportTypeTemp.toUpperCase() == "SRA" ||
        selectedFileReportTypeTemp.toUpperCase() == "ZON" ||
        selectedFileReportTypeTemp.toUpperCase() == "APPR") {
        this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
      }

      //Make sure ReportType is PCA

      if (selectedFileReportTypeTemp) {
        if (selectedFileReportTypeTemp.toUpperCase() == "PCA") {
          this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
        } else if (selectedFileReportTypeTemp.toUpperCase() == "APPR" ||
          selectedFileReportTypeTemp.toUpperCase() == "ESA" ||
          selectedFileReportTypeTemp.toUpperCase() == "SRA" ||
          selectedFileReportTypeTemp.toUpperCase() == "ZON") {
          this.rejectedFiles.push(event.files[i].name);
          this.disableUpload = true;

        } else {
          this.selectedFileReportType = "";
          this.rejectedFiles.push(event.files[i].name);
          this.disableUpload = true;
        }
      } else {
        this.selectedFileReportType = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Validate Deal ID
      //Make sure DealID is numeric and check Deal ID
      selectedFileDealIdTemp = event.files[i].name.split("_")[0];
      if (isNumeric(selectedFileDealIdTemp) &&
        (!this.docWayDetailsService.hasDealId)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else if (this.docWayDetailsService.hasDealId &&
        (selectedFileDealIdTemp == this.docWayDetailsService.mf_deal_id) &&
        isNumeric(selectedFileDealIdTemp)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }
      //Add selected files to table
      this.selectedFiles.push({
        fileName: event.files[i].name,
        fileType: event.files[i].name.split(".")[event.files[i].name.split(".").length - 1],
        reportType: this.selectedFileReportType,
        dealId: this.selectedFileDealId
      });

      //Limit number of files uploaded per upload
      if (this.selectedFiles.length > AppConstants.MAX_FILE_UPLOAD_LIMIT)
        this.disableUpload = true;
    }
  }


  // INS and Property Specific Check
  async processSelectedFiles_Insurance(
    event: any,
    dusDealId: string,
    selectedPropertyName: string,
    coltRefNo?: string,
    aqsnDealId?: string,
    propMstrNo?: string,
    transType?: string,
    ssn?: string,
    propId?: string,
    cmmtNo?: string
  ) {
    console.log("Selected Property Name in File Upload Service: " + selectedPropertyName);
    let selectedFileReportTypeTemp: string;
    let selectedFileDealIdTemp: string;
    let selectedFilePropertyNameTemp: string;
    this.disableUpload = false;
    this.propertyIDMatch = true;
    this.propertyNameMatch = true;
    this.reportTypeMatch = true;
    this.fileTypeMatch = true;
    this.rejectedFiles = [];

    for (let i = 0; i < event.files.length; i++) {
      //Make sure ReportType is INS and check with Docway value if available
      selectedFileReportTypeTemp = event.files[i].name.split(".")[0];
      this.selectedFileFormat = event.files[i].name.split(".")[1].toUpperCase();
      selectedFileReportTypeTemp = selectedFileReportTypeTemp.split("_")[1];
      const fileNameParts = event.files[i].name.split("_");
      selectedFilePropertyNameTemp = fileNameParts.slice(2).join("_").replace(/\s+/g, '').replace(/[\._].*$/, '');


      if (selectedFileReportTypeTemp) {
        if (this.docWayDetailsService.hasReportType) {
          if (selectedFileReportTypeTemp.toUpperCase() == "INS" && this.docWayDetailsService.mf_doc_type == "Insurance Report")
            this.selectedFileReportType = "INS";
          else {
            this.selectedFileReportType = "";
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
          }
        } else {
          if (selectedFileReportTypeTemp.toUpperCase() == "INS" ||
            selectedFileReportTypeTemp.toUpperCase() == "A25" ||
            selectedFileReportTypeTemp.toUpperCase() == "A28" ||
            selectedFileReportTypeTemp.toUpperCase() == "ALTA" ||
            selectedFileReportTypeTemp.toUpperCase() == "FAW" ||
            selectedFileReportTypeTemp.toUpperCase() == "LIA" ||
            selectedFileReportTypeTemp.toUpperCase() == "NFIP" ||
            selectedFileReportTypeTemp.toUpperCase() == "PFX" ||
            selectedFileReportTypeTemp.toUpperCase() == "PROP" ||
            selectedFileReportTypeTemp.toUpperCase() == "SFHDF" ||
            selectedFileReportTypeTemp.toUpperCase() == "SOV" ||
            selectedFileReportTypeTemp.toUpperCase() == "PROPLIA") {
            this.selectedFileReportType = selectedFileReportTypeTemp.toUpperCase();
          } else {
            this.selectedFileReportType = "";
            this.rejectedFiles.push(event.files[i].name);
            this.disableUpload = true;
            this.reportTypeMatch = false;
          }
        }
      } else {
        this.selectedFileReportType = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Check if INS file is JSON
      if (this.selectedFileReportType == "INS" && this.selectedFileFormat != "JSON") {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
        this.fileTypeMatch = false;
      }

      //Check if file is one of the allowed formats for other report types
      const reportTypes = ["A25", "A28", "ALTA", "FAW", "LIA", "NFIP", "PFX", "PROP", "SFHDF", "SOV", "PROPLIA"];
      const allowedFormats = ["PDF", "XLS", "XLSX", "DOC", "DOCX", "XLSM"];

      if (reportTypes.includes(this.selectedFileReportType) && !allowedFormats.includes(this.selectedFileFormat)) {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
        this.fileTypeMatch = false;
      }

      //Validate Deal ID
      //Make sure DealID is numeric and check Deal ID
      selectedFileDealIdTemp = event.files[i].name.split("_")[0];
      if (isNumeric(selectedFileDealIdTemp) &&
        (!this.docWayDetailsService.hasDealId)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else if (this.docWayDetailsService.hasDealId &&
        (selectedFileDealIdTemp == this.docWayDetailsService.mf_deal_id) &&
        isNumeric(selectedFileDealIdTemp)) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
      }

      //Make sure DealID matches selected property
      if (dusDealId == selectedFileDealIdTemp) {
        this.selectedFileDealId = selectedFileDealIdTemp;
      } else {
        this.selectedFileDealId = "";
        if (!this.disableUpload)
          this.rejectedFiles.push(event.files[i].name);
        this.disableUpload = true;
        this.propertyIDMatch = false;
      }

      selectedPropertyName = selectedPropertyName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      selectedFilePropertyNameTemp = selectedFilePropertyNameTemp.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

      console.log("Selected File Property Name: " + selectedFilePropertyNameTemp);
      console.log("Selected File Report Type: " + selectedPropertyName);
      //Make sure Property Name matches selected property
      if (this.selectedFileReportType !== "INS") {
        if (selectedPropertyName.replace(/\s+/g, '').toUpperCase() == selectedFilePropertyNameTemp.toUpperCase()) {
          this.selectedFilePropertyName = selectedFilePropertyNameTemp;
        } else {
          this.selectedFilePropertyName = "";
          if (!this.disableUpload)
            this.rejectedFiles.push(event.files[i].name);
          this.disableUpload = true;
          this.propertyNameMatch = false;
        }
      }
      //check for user has access to docway - call docway presigned url
      if (this.selectedFileReportType !== "INS" && this.propertyNameMatch !== false
        && this.propertyIDMatch !== false && this.reportTypeMatch !== false && this.fileTypeMatch !== false) {
        // Capture file data before async call to avoid closure issues
        const currentFileName = event.files[i].name;
        const currentFileType = event.files[i].name.split(".")[event.files[i].name.split(".").length - 1];
        const currentReportType = this.selectedFileReportType;
        const currentDealId = this.selectedFileDealId;

        let params = new HttpParams()
          .set('filename', currentFileName)
          .set('userid', this.apiService.getUserDetails().uid)
          .set('usertype', this.apiService.getUserDetails().userType)
          .set('username', this.apiService.getUserDetails().firstName + ' ' + this.apiService.getUserDetails().lastName)
          .set('email', this.apiService.getUserDetails().mail)
          .set('org', this.apiService.getUserDetails().subscriberName)
          .set('selrsrvrnolist', this.apiService.getSellerServicerNumber().sellerServicerNo)
          .set('cfmlycode', this.apiService.getUserDetails().corporateFamilyCode)
          .set('version', '2')
          .set('propertyname', selectedPropertyName)
          .set('dusdealid', dusDealId)
          .set('coltrefno', coltRefNo || 'NULL')
          .set('aqsndealid', aqsnDealId || 'NULL')
          .set('propmstrno', propMstrNo || 'NULL')
          .set('propid', propId || 'NULL')
          .set('ssn', ssn || 'NULL')
          .set('cmmtno', cmmtNo || 'NULL')
          .set('transtype', transType || 'NULL')
          .set('feduserid', this.apiService.getUserDetails().fmFederationID);

        let url = this.endpointsService.setApiPath('docway') + this.endpointsService.getEndpoints().getdocwayPreAuth;

        try {
          const body: any = await this.dashboardService.getDocwayPreAuth(url, params).toPromise();
          console.log('Docway PreAuth Response:', body);
          if (body === 'AUTHORIZED') {
            this.isDocwayUser = true;
            // Process file ONLY if authorized
            this.selectedFiles.push({
              fileName: currentFileName,
              fileType: currentFileType,
              reportType: currentReportType,
              dealId: currentDealId
            });
          } else if (body === 'NOT AUTHORIZED') {
            this.rejectedFiles.push(currentFileName);
            this.disableUpload = true;
            this.isDocwayUser = false;
            // Show error notification after response
            this.showDocwayAuthError("", AppConstants.REPORT_TYPE_ERROR_MSG, AppConstants.FILE_UPLOAD_ERROR_NOTE_DOCWAY);
          } else {
            this.rejectedFiles.push(currentFileName);
            this.disableUpload = true;
            this.isDocwayUser = false;
            // Show error notification after response
            this.showDocwayAuthError("", AppConstants.DOCWAY_DOWN_ERROR_MSG, "");
          }
        } catch (err) {
          console.error('Docway PreAuth Error:', err);
          this.rejectedFiles.push(currentFileName);
          this.isDocwayUser = false;
          this.disableUpload = true;
          // Show error notification after error
          this.showDocwayAuthError("", AppConstants.REPORT_TYPE_ERROR_MSG, AppConstants.FILE_UPLOAD_ERROR_NOTE_DOCWAY);
        }
      } else if (this.selectedFileReportType === "INS" && this.propertyNameMatch !== false && this.propertyIDMatch !== false && this.reportTypeMatch !== false && this.fileTypeMatch !== false) {
        // INS files don't require DocWay authorization - add them directly
        this.selectedFiles.push({
          fileName: event.files[i].name,
          fileType: event.files[i].name.split(".")[event.files[i].name.split(".").length - 1],
          reportType: this.selectedFileReportType,
          dealId: this.selectedFileDealId
        });
      }

      //Limit number of files uploaded per upload
      if (this.selectedFiles.length > AppConstants.MAX_FILE_UPLOAD_LIMIT)
        this.disableUpload = true;
    }
  }

  onClearFile() {
    this.fileSelected = false;
    this.selectedFiles = [];
    this.disableUpload = false;
    this.rejectedFiles = [];
  }

  setReviewFileSummaryTableData(data: any): void {
    this.reviewFileSummaryTableDataSubject.next(data);
  }

  // /**
  //  * Call API endpoint and return the response string
  //  * @param endpoint - The API endpoint path (e.g., '/yourEndpoint')
  //  * @param params - Optional query parameters
  //  * @returns Observable<string> - The API response as a string
  //  */
  // public callApiEndpoint(endpoint: string, params?: { [key: string]: string }): Observable<string> {
  //   const url = this.endpointsService.setApiPath('dd') + endpoint;

  //   let httpParams = new HttpParams();
  //   if (params) {
  //     Object.keys(params).forEach(key => {
  //       httpParams = httpParams.set(key, params[key]);
  //     });
  //   }

  //   return this.httpClient.get(url, { params: httpParams, responseType: 'text' }).pipe(
  //     map((response: string) => {
  //       return response;
  //     }),
  //     catchError(err => {
  //       console.error('API call error:', err);
  //       throw err;
  //     })
  //   );
  // }


}
